export interface RazorpayCreatePaymentPayload {
  amount: number;
  currency: string;
  order_id: string;
  email: string;
  contact: string;
  callback_url: string;
  method?: 'card' | 'netbanking' | 'upi';
  customer_id?: string;
  bank?: string;
  recurring?: number;
  save?: number;
  upi?: {
    qr: boolean;
    timeout: number;
  };
  [key: string]: unknown;
}

export interface RazorpayInstance {
  createPayment(
    payload: RazorpayCreatePaymentPayload,
    options?: { app?: string }
  ): void;
  once?: (
    eventName: 'ready',
    handler: (response: { methods?: { netbanking?: unknown } }) => void
  ) => void;
}

export interface RazorpayFormatterField {
  on(
    eventName: 'change' | 'network',
    handler: (this: {
      el: HTMLInputElement;
      isValid: () => boolean;
      maxLen?: number;
      type?: string;
    }) => void
  ): RazorpayFormatterField;
}

export interface RazorpayFormatter {
  add(type: 'card' | 'expiry' | 'cvv', element: HTMLInputElement): RazorpayFormatterField;
}

export interface RazorpayConstructor {
  new (options: {
    key: string;
    redirect?: boolean;
    webview_intent?: boolean;
  }): RazorpayInstance;
  setFormatter?: (form: HTMLFormElement) => RazorpayFormatter;
}

declare global {
  interface Window {
    Razorpay?: RazorpayConstructor;
  }
}

let razorpaySdkPromise: Promise<RazorpayConstructor> | null = null;
const RAZORPAY_DIRECT_SDK_URL = 'https://checkout.razorpay.com/v1/razorpay.js';

function getDirectRazorpaySdk(): RazorpayConstructor | null {
  const RazorpayClass = window.Razorpay;
  if (!RazorpayClass) return null;

  try {
    const instance = new RazorpayClass({ key: 'rzp_test_probe' });
    return typeof instance.createPayment === 'function' ? RazorpayClass : null;
  } catch {
    return null;
  }
}

export function loadRazorpaySdk(): Promise<RazorpayConstructor> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Razorpay SDK can only be loaded in the browser.'));
  }

  const directRazorpaySdk = getDirectRazorpaySdk();
  if (directRazorpaySdk) {
    return Promise.resolve(directRazorpaySdk);
  }

  if (razorpaySdkPromise) {
    return razorpaySdkPromise;
  }

  razorpaySdkPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${RAZORPAY_DIRECT_SDK_URL}"]`
    );

    if (existingScript) {
      existingScript.addEventListener('load', () => {
        const loadedRazorpaySdk = getDirectRazorpaySdk();
        if (loadedRazorpaySdk) {
          resolve(loadedRazorpaySdk);
        } else {
          reject(new Error('Razorpay direct SDK is not available.'));
        }
      });
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Razorpay SDK.')));
      return;
    }

    const script = document.createElement('script');
    script.src = RAZORPAY_DIRECT_SDK_URL;
    script.async = true;
    script.onload = () => {
      const loadedRazorpaySdk = getDirectRazorpaySdk();
      if (loadedRazorpaySdk) {
        resolve(loadedRazorpaySdk);
      } else {
        reject(new Error('Razorpay direct SDK is not available.'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load Razorpay SDK.'));
    document.head.appendChild(script);
  });

  return razorpaySdkPromise;
}
